import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Shield, Server, Clock, Eye, Plus, AlertTriangle, CheckCircle, XCircle, 
  Activity, Download, Settings, X
} from 'lucide-react';

// -------------------- Styled Components --------------------
const Container = styled.div`
  background: #f8fafc;
  min-height: 100vh;
  width: 100%;
  max-width: 1800px;
  margin: 0 auto;
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 14px;
`;

const Header = styled.div`
  background: white;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const NewScanButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  transition: all 0.2s;

  &:hover {
    background: #2563eb;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 0.5rem;
    span {
      display: none;
    }
  }
`;

const ExportReportButton = styled(NewScanButton)`
  background: #10b981;

  &:hover {
    background: #059669;
  }
`;

const MainContent = styled.div`
  padding: 1.5rem 2rem;
  background: white;
  min-height: calc(100vh - 100px);
  max-width: 1800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const TabsContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const TabHeader = styled.div`
  display: flex;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 1.5rem;
  overflow-x: auto;
  scrollbar-width: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button`
  padding: 0.75rem 1.25rem;
  border: none;
  background: transparent;
  color: ${props => (props.active ? '#3b82f6' : '#64748b')};
  font-weight: ${props => (props.active ? '700' : '600')};
  cursor: pointer;
  border-bottom: ${props => (props.active ? '3px solid #3b82f6' : '3px solid transparent')};
  transition: all 0.25s ease;
  font-size: 0.8125rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  &:hover {
    color: #3b82f6;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const StatCard = styled.div`
  background: #fefefe;
  padding: 1.25rem 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 6px rgb(0 0 0 / 0.08);
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatIcon = styled.div`
  background: ${props => props.bgColor || '#3b82f6'};
  color: white;
  padding: 0.5rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
`;

const StatTitle = styled.div`
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #475569;
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 800;
  color: ${props => props.color || '#0f172a'};
`;

const StatSubtext = styled.div`
  font-size: 0.75rem;
  color: #64748b;
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    border-radius: 0;
    margin-left: -1rem;
    margin-right: -1rem;
    width: calc(100% + 2rem);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
`;

const Thead = styled.thead`
  background: #f8fafc;
`;

const Tr = styled.tr`
  &:hover {
    background: #f1f5f9;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  font-weight: 700;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 0.75rem 1rem;
  color: #475569;
  vertical-align: middle;
  border-bottom: 1px solid #f1f5f9;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: ${props => {
    switch (props.status?.toLowerCase()) {
      case 'completed': return '#166534';
      case 'running': return '#1e40af';
      case 'failed': return '#b91c1c';
      case 'scheduled': return '#b45309';
      case 'paused': return '#475569';
      default: return '#475569';
    }
  }};
  background-color: ${props => {
    switch (props.status?.toLowerCase()) {
      case 'completed': return '#dcfce7';
      case 'running': return '#dbeafe';
      case 'failed': return '#fee2e2';
      case 'scheduled': return '#fef3c7';
      case 'paused': return '#f1f5f9';
      default: return '#f1f5f9';
    }
  }};
`;

const VulnBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 0.4rem;
  font-weight: 700;
  font-size: 0.6875rem;
  color: white;
  background-color: ${props => {
    switch (props.severity) {
      case 'critical': return '#7f1d1d';
      case 'high': return '#dc2626';
      case 'medium': return '#d97706';
      case 'low': return '#059669';
      case 'info': return '#2563eb';
      default: return '#64748b';
    }
  }};
  margin-right: 0.25rem;
`;

const ActionButton = styled.button`
  background: transparent;
  border: 1px solid #cbd5e1;
  color: #475569;
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: #f8fafc;
    border-color: #94a3b8;
    color: #1e293b;
  }

  @media (max-width: 768px) {
    padding: 0.25rem;
    span {
      display: none;
    }
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  background: #e2e8f0;
  border-radius: 9999px;
  height: 0.5rem;
  margin-top: 0.5rem;
`;

const ProgressFill = styled.div`
  background: #3b82f6;
  height: 100%;
  border-radius: 9999px;
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  font-size: 0.6875rem;
  color: #64748b;
  margin-top: 0.25rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  width: 100%;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 0.75rem;
  box-shadow: 0 4px 15px rgba(0,0,0,0.15);
  margin: 1rem;

  @media (max-width: 768px) {
    max-width: 100%;
    margin: 0;
    border-radius: 0;
    max-height: 100vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background: white;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const ModalTitle = styled.h2`
  font-weight: 700;
  font-size: 1.5rem;
  color: #0f172a;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  font-weight: 700;
  color: #475569;
  cursor: pointer;
  padding: 0.25rem;
  transition: color 0.2s;

  &:hover {
    color: #0f172a;
  }
`;

const FormSection = styled.div`
  margin-bottom: 1.5rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  background: white;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
`;

const SubmitButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 0.8125rem;

  &:hover {
    background: #2563eb;
  }
`;

const CancelButton = styled.button`
  background: transparent;
  color: #6b7280;
  border: 1px solid #d1d5db;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.8125rem;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;

// -------------------- Mock Data --------------------
const mockScans = [
  {
    id: 'SCAN-2025-001',
    name: 'Production Infrastructure Scan',
    targetGroup: 'Production Servers',
    assets: 24,
    status: 'completed',
    startTime: '2025-01-15 14:30:00',
    endTime: '2025-01-15 16:45:30',
    duration: '2h 15m',
    vulnerabilities: { critical: 3, high: 12, medium: 28, low: 45, info: 18 },
    scanType: 'Authenticated',
    scanner: 'Agent-Based',
    progress: 100
  },
  {
    id: 'SCAN-2025-002',
    name: 'DMZ Network Assessment',
    targetGroup: 'DMZ Servers',
    assets: 18,
    status: 'running',
    startTime: '2025-01-16 09:15:00',
    endTime: null,
    duration: '1h 32m',
    vulnerabilities: { critical: 1, high: 5, medium: 12, low: 8, info: 3 },
    scanType: 'Credentialed',
    scanner: 'Network-Based',
    progress: 67
  },
  {
    id: 'SCAN-2025-003',
    name: 'Development Environment Scan',
    targetGroup: 'Dev Servers',
    assets: 8,
    status: 'scheduled',
    startTime: '2025-01-17 02:00:00',
    endTime: null,
    duration: null,
    vulnerabilities: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
    scanType: 'Unauthenticated',
    scanner: 'Agent-Based',
    progress: 0
  },
  {
    id: 'SCAN-2025-004',
    name: 'Web Application Security Test',
    targetGroup: 'Web Servers',
    assets: 12,
    status: 'failed',
    startTime: '2025-01-14 22:00:00',
    endTime: '2025-01-14 22:15:00',
    duration: '15m',
    vulnerabilities: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
    scanType: 'Web Application',
    scanner: 'DAST',
    progress: 0
  }
];

const mockAssets = [
  { hostname: 'web-prod-01.company.com', ip: '192.168.1.10', os: 'Ubuntu 20.04.3 LTS', services: 5, vulnerabilities: 12 },
  { hostname: 'db-prod-01.company.com', ip: '192.168.1.11', os: 'CentOS 7.9', services: 3, vulnerabilities: 8 },
  { hostname: 'app-prod-01.company.com', ip: '192.168.1.12', os: 'Windows Server 2019', services: 8, vulnerabilities: 15 },
  { hostname: 'mail-prod-01.company.com', ip: '192.168.1.13', os: 'Ubuntu 22.04.1 LTS', services: 4, vulnerabilities: 6 },
  { hostname: 'proxy-prod-01.company.com', ip: '192.168.1.14', os: 'pfSense 2.6.0', services: 2, vulnerabilities: 3 },
  { hostname: 'backup-prod-01.company.com', ip: '192.168.1.15', os: 'FreeBSD 13.1', services: 3, vulnerabilities: 4 },
  { hostname: 'monitor-prod-01.company.com', ip: '192.168.1.16', os: 'Ubuntu 22.04.1 LTS', services: 6, vulnerabilities: 7 }
];

const mockVulnerabilities = [
  { 
    id: 'CVE-2023-12345', 
    title: 'Remote Code Execution in Apache HTTP Server', 
    severity: 'critical', 
    cvss: 9.8,
    affectedHosts: 4,
    description: 'A critical vulnerability allowing remote code execution',
    solution: 'Update Apache HTTP Server to version 2.4.54 or later'
  },
  { 
    id: 'CVE-2023-67890', 
    title: 'SQL Injection in Custom Application', 
    severity: 'critical', 
    cvss: 9.1,
    affectedHosts: 2,
    description: 'SQL injection vulnerability in user authentication',
    solution: 'Implement parameterized queries and input validation'
  },
  { 
    id: 'CVE-2023-11111', 
    title: 'Privilege Escalation in Linux Kernel', 
    severity: 'high', 
    cvss: 8.4,
    affectedHosts: 8,
    description: 'Local privilege escalation vulnerability',
    solution: 'Update Linux kernel to latest stable version'
  },
  { 
    id: 'CVE-2023-22222', 
    title: 'Cross-Site Scripting (XSS)', 
    severity: 'medium', 
    cvss: 6.1,
    affectedHosts: 5,
    description: 'Stored XSS vulnerability in web application',
    solution: 'Implement proper input sanitization and output encoding'
  },
  { 
    id: 'CVE-2023-33333', 
    title: 'Information Disclosure', 
    severity: 'low', 
    cvss: 3.7,
    affectedHosts: 12,
    description: 'Sensitive information exposed in error messages',
    solution: 'Configure proper error handling and logging'
  },
  { 
    id: 'CVE-2023-44444', 
    title: 'Weak SSL/TLS Configuration', 
    severity: 'medium', 
    cvss: 5.3,
    affectedHosts: 6,
    description: 'Weak cipher suites and protocols enabled',
    solution: 'Update SSL/TLS configuration to use strong ciphers'
  },
  { 
    id: 'CVE-2023-55555', 
    title: 'Directory Traversal', 
    severity: 'high', 
    cvss: 7.5,
    affectedHosts: 3,
    description: 'Path traversal vulnerability in file upload',
    solution: 'Implement proper file path validation'
  }
];

const tabs = [
  { id: 'scans', label: 'Scans', icon: Shield },
  { id: 'assets', label: 'Assets', icon: Server },
  { id: 'vulnerabilities', label: 'Vulnerabilities', icon: AlertTriangle },
  { id: 'compliance', label: 'Compliance', icon: CheckCircle },
];

// -------------------- Components --------------------
const StatusIcon = ({ status }) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return <CheckCircle size={12} />;
    case 'running':
      return <Activity size={12} />;
    case 'failed':
      return <XCircle size={12} />;
    case 'scheduled':
      return <Clock size={12} />;
    default:
      return <Clock size={12} />;
  }
};

const NewScanModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    scanName: '',
    targetGroup: '',
    ipRange: '',
    scanType: 'authenticated',
    scanner: 'agent-based',
    schedule: 'immediate',
    scheduledTime: '',
    description: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Create New Scan</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <form onSubmit={handleSubmit}>
            <FormSection>
              <SectionTitle>Basic Information</SectionTitle>
              <FormGrid>
                <FormGroup>
                  <label htmlFor="scanName">Scan Name *</label>
                  <Input
                    type="text"
                    id="scanName"
                    name="scanName"
                    value={formData.scanName}
                    onChange={handleChange}
                    placeholder="e.g., Production Infrastructure Scan"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <label htmlFor="targetGroup">Target Group *</label>
                  <Select
                    id="targetGroup"
                    name="targetGroup"
                    value={formData.targetGroup}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select target group</option>
                    <option value="production">Production Servers</option>
                    <option value="dmz">DMZ Servers</option>
                    <option value="development">Development Servers</option>
                    <option value="web">Web Servers</option>
                    <option value="database">Database Servers</option>
                    <option value="custom">Custom Range</option>
                  </Select>
                </FormGroup>
              </FormGrid>
              
              <FormGroup>
                <label htmlFor="ipRange">IP Range/Targets *</label>
                <Input
                  type="text"
                  id="ipRange"
                  name="ipRange"
                  value={formData.ipRange}
                  onChange={handleChange}
                  placeholder="e.g., 192.168.1.0/24 or 10.0.0.1-10.0.0.50"
                  required
                />
              </FormGroup>
            </FormSection>

            <FormSection>
              <SectionTitle>Scan Configuration</SectionTitle>
              <FormGrid>
                <FormGroup>
                  <label htmlFor="scanType">Scan Type</label>
                  <Select
                    id="scanType"
                    name="scanType"
                    value={formData.scanType}
                    onChange={handleChange}
                  >
                    <option value="authenticated">Authenticated Scan</option>
                    <option value="unauthenticated">Unauthenticated Scan</option>
                    <option value="credentialed">Credentialed Scan</option>
                    <option value="web-application">Web Application Scan</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <label htmlFor="scanner">Scanner Type</label>
                  <Select
                    id="scanner"
                    name="scanner"
                    value={formData.scanner}
                    onChange={handleChange}
                  >
                    <option value="agent-based">Agent-Based</option>
                    <option value="network-based">Network-Based</option>
                    <option value="dast">DAST</option>
                    <option value="sast">SAST</option>
                  </Select>
                </FormGroup>
              </FormGrid>
            </FormSection>

            <FormSection>
              <SectionTitle>Schedule</SectionTitle>
              <FormGrid>
                <FormGroup>
                  <label htmlFor="schedule">When to run</label>
                  <Select
                    id="schedule"
                    name="schedule"
                    value={formData.schedule}
                    onChange={handleChange}
                  >
                    <option value="immediate">Run Immediately</option>
                    <option value="scheduled">Schedule for Later</option>
                  </Select>
                </FormGroup>
                {formData.schedule === 'scheduled' && (
                  <FormGroup>
                    <label htmlFor="scheduledTime">Scheduled Time</label>
                    <Input
                      type="datetime-local"
                      id="scheduledTime"
                      name="scheduledTime"
                      value={formData.scheduledTime}
                      onChange={handleChange}
                    />
                  </FormGroup>
                )}
              </FormGrid>
            </FormSection>

            <FormSection>
              <FormGroup>
                <label htmlFor="description">Description (Optional)</label>
                <TextArea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Additional notes about this scan..."
                />
              </FormGroup>
            </FormSection>

            <FormActions>
              <CancelButton type="button" onClick={onClose}>
                Cancel
              </CancelButton>
              <SubmitButton type="submit">
                {formData.schedule === 'immediate' ? 'Start Scan' : 'Schedule Scan'}
              </SubmitButton>
            </FormActions>
          </form>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

const ScanReportModal = ({ onClose, scan }) => {
  if (!scan) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Scan Report: {scan.name}</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <FormSection>
            <SectionTitle>Executive Summary</SectionTitle>
            <StatGrid>
              <StatCard>
                <StatHeader>
                  <StatIcon bgColor="#dc2626">
                    <AlertTriangle size={20} />
                  </StatIcon>
                  <StatTitle>Critical</StatTitle>
                </StatHeader>
                <StatValue color="#dc2626">{scan.vulnerabilities.critical}</StatValue>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon bgColor="#ea580c">
                    <AlertTriangle size={20} />
                  </StatIcon>
                  <StatTitle>High</StatTitle>
                </StatHeader>
                <StatValue color="#ea580c">{scan.vulnerabilities.high}</StatValue>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon bgColor="#ca8a04">
                    <AlertTriangle size={20} />
                  </StatIcon>
                  <StatTitle>Medium</StatTitle>
                </StatHeader>
                <StatValue color="#ca8a04">{scan.vulnerabilities.medium}</StatValue>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon bgColor="#16a34a">
                    <CheckCircle size={20} />
                  </StatIcon>
                  <StatTitle>Low</StatTitle>
                </StatHeader>
                <StatValue color="#16a34a">{scan.vulnerabilities.low}</StatValue>
              </StatCard>
            </StatGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>Scan Details</SectionTitle>
            <FormGrid>
              <div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <strong>Scan ID:</strong> <span style={{ fontFamily: 'monospace' }}>{scan.id}</span>
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <strong>Target Group:</strong> {scan.targetGroup}
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <strong>Assets Scanned:</strong> {scan.assets}
                </div>
              </div>
              <div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <strong>Scan Type:</strong> {scan.scanType}
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <strong>Duration:</strong> {scan.duration}
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <strong>Status:</strong> <StatusBadge status={scan.status}><StatusIcon status={scan.status} />{scan.status}</StatusBadge>
                </div>
              </div>
            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>Top Vulnerabilities</SectionTitle>
            <TableWrapper>
              <Table>
                <Thead>
                  <Tr>
                    <Th>CVE ID</Th>
                    <Th>Title</Th>
                    <Th>Severity</Th>
                    <Th>CVSS</Th>
                    <Th>Affected Hosts</Th>
                  </Tr>
                </Thead>
                <tbody>
                  {mockVulnerabilities.slice(0, 5).map((vuln) => (
                    <Tr key={vuln.id}>
                      <Td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{vuln.id}</Td>
                      <Td>{vuln.title}</Td>
                      <Td>
                        <VulnBadge severity={vuln.severity}>{vuln.severity.toUpperCase()}</VulnBadge>
                      </Td>
                      <Td style={{ fontWeight: '700' }}>{vuln.cvss}</Td>
                      <Td>{vuln.affectedHosts}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          </FormSection>

          <FormSection>
            <SectionTitle>Affected Assets</SectionTitle>
            <TableWrapper>
              <Table>
                <Thead>
                  <Tr>
                    <Th>Hostname</Th>
                    <Th>IP Address</Th>
                    <Th>Operating System</Th>
                    <Th>Services</Th>
                    <Th>Vulnerabilities</Th>
                  </Tr>
                </Thead>
                <tbody>
                  {mockAssets.map((asset, index) => (
                    <Tr key={index}>
                      <Td style={{ fontWeight: '600' }}>{asset.hostname}</Td>
                      <Td style={{ fontFamily: 'monospace' }}>{asset.ip}</Td>
                      <Td>{asset.os}</Td>
                      <Td>{asset.services}</Td>
                      <Td style={{ fontWeight: '700', color: '#dc2626' }}>{asset.vulnerabilities}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          </FormSection>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

const PlaceholderContent = ({ icon: Icon, title, description }) => (
  <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
    <Icon size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
    <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', fontWeight: '600' }}>{title}</h3>
    <p style={{ margin: 0, opacity: 0.8 }}>{description}</p>
  </div>
);

// -------------------- Main Component --------------------
const InternalScansPage = () => {
  const [activeTab, setActiveTab] = useState('scans');
  const [showReportModal, setShowReportModal] = useState(false);
  const [showNewScanModal, setShowNewScanModal] = useState(false);
  const [selectedScan, setSelectedScan] = useState(null);

  const handleViewReport = (scan) => {
    setSelectedScan(scan);
    setShowReportModal(true);
  };

  const handleNewScan = (formData) => {
    console.log('New scan created:', formData);
    // Here you would typically send the data to your backend
  };

  const totalVulns = mockScans.reduce((acc, scan) => {
    return acc + scan.vulnerabilities.critical + scan.vulnerabilities.high + 
           scan.vulnerabilities.medium + scan.vulnerabilities.low + scan.vulnerabilities.info;
  }, 0);

  const runningScans = mockScans.filter(scan => scan.status === 'running').length;
  const criticalVulns = mockScans.reduce((acc, scan) => acc + scan.vulnerabilities.critical, 0);

  return (
    <Container>
      <Header>
        <HeaderContent>
          <Title>Vulnerability Management</Title>
          <HeaderActions>
            <NewScanButton onClick={() => setShowNewScanModal(true)}>
              <Plus size={20} />
              <span>New Scan</span>
            </NewScanButton>
            <ExportReportButton>
              <Download size={20} />
              <span>Export Report</span>
            </ExportReportButton>
          </HeaderActions>
        </HeaderContent>
      </Header>

      <MainContent>
        <TabsContainer>
          <TabHeader>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Tab
                  key={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon size={18} />
                  {tab.label}
                </Tab>
              );
            })}
          </TabHeader>
        </TabsContainer>

        {activeTab === 'scans' && (
          <>
            <StatGrid>
              <StatCard>
                <StatHeader>
                  <StatIcon bgColor="#3b82f6">
                    <Shield size={22} />
                  </StatIcon>
                  <StatTitle>Total Scans</StatTitle>
                </StatHeader>
                <StatValue>{mockScans.length}</StatValue>
                <StatSubtext>All time</StatSubtext>
              </StatCard>

              <StatCard>
                <StatHeader>
                  <StatIcon bgColor="#10b981">
                    <Activity size={22} />
                  </StatIcon>
                  <StatTitle>Running Scans</StatTitle>
                </StatHeader>
                <StatValue color="#10b981">{runningScans}</StatValue>
                <StatSubtext>Currently active</StatSubtext>
              </StatCard>

              <StatCard>
                <StatHeader>
                  <StatIcon bgColor="#dc2626">
                    <AlertTriangle size={22} />
                  </StatIcon>
                  <StatTitle>Critical Vulnerabilities</StatTitle>
                </StatHeader>
                <StatValue color="#dc2626">{criticalVulns}</StatValue>
                <StatSubtext>Require immediate attention</StatSubtext>
              </StatCard>

              <StatCard>
                <StatHeader>
                  <StatIcon bgColor="#7c3aed">
                    <Settings size={22} />
                  </StatIcon>
                  <StatTitle>Total Vulnerabilities</StatTitle>
                </StatHeader>
                <StatValue>{totalVulns}</StatValue>
                <StatSubtext>Across all scans</StatSubtext>
              </StatCard>
            </StatGrid>

            <SectionTitle>Recent Scans</SectionTitle>
            <TableWrapper>
              <Table>
                <Thead>
                  <Tr>
                    <Th>Scan Details</Th>
                    <Th>Target</Th>
                    <Th>Status</Th>
                    <Th>Vulnerabilities</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <tbody>
                  {mockScans.map((scan) => (
                    <Tr key={scan.id}>
                      <Td>
                        <div style={{ fontWeight: '700', color: '#1e293b', marginBottom: '0.25rem' }}>
                          {scan.name}
                        </div>
                        <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>
                          {scan.id}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {scan.scanType}
                        </div>
                      </Td>
                      <Td>
                        <div style={{ fontWeight: '600' }}>{scan.targetGroup}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{scan.assets} assets</div>
                      </Td>
                      <Td>
                        <StatusBadge status={scan.status}>
                          <StatusIcon status={scan.status} />
                          {scan.status}
                        </StatusBadge>
                        {scan.status === 'running' && (
                          <div>
                            <ProgressBar>
                              <ProgressFill progress={scan.progress} />
                            </ProgressBar>
                            <ProgressText>{scan.progress}% complete</ProgressText>
                          </div>
                        )}
                      </Td>
                      <Td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {scan.vulnerabilities.critical > 0 && (
                            <VulnBadge severity="critical">{scan.vulnerabilities.critical}C</VulnBadge>
                          )}
                          {scan.vulnerabilities.high > 0 && (
                            <VulnBadge severity="high">{scan.vulnerabilities.high}H</VulnBadge>
                          )}
                          {scan.vulnerabilities.medium > 0 && (
                            <VulnBadge severity="medium">{scan.vulnerabilities.medium}M</VulnBadge>
                          )}
                          {scan.vulnerabilities.low > 0 && (
                            <VulnBadge severity="low">{scan.vulnerabilities.low}L</VulnBadge>
                          )}
                          {scan.vulnerabilities.info > 0 && (
                            <VulnBadge severity="info">{scan.vulnerabilities.info}I</VulnBadge>
                          )}
                        </div>
                      </Td>
                      <Td>
                        <ActionButton onClick={() => handleViewReport(scan)}>
                          <Eye size={16} />
                          <span>View Report</span>
                        </ActionButton>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          </>
        )}

        {activeTab === 'assets' && (
          <>
            <SectionTitle>Asset Inventory</SectionTitle>
            <TableWrapper>
              <Table>
                <Thead>
                  <Tr>
                    <Th>Asset Details</Th>
                    <Th>Operating System</Th>
                    <Th>Services</Th>
                    <Th>Vulnerabilities</Th>
                  </Tr>
                </Thead>
                <tbody>
                  {mockAssets.map((asset, index) => (
                    <Tr key={index}>
                      <Td>
                        <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{asset.hostname}</div>
                        <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#64748b' }}>{asset.ip}</div>
                      </Td>
                      <Td>{asset.os}</Td>
                      <Td>{asset.services} services</Td>
                      <Td style={{ fontWeight: '700', color: '#dc2626' }}>{asset.vulnerabilities}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          </>
        )}

        {activeTab === 'vulnerabilities' && (
          <>
            <SectionTitle>Vulnerability Database</SectionTitle>
            <TableWrapper>
              <Table>
                <Thead>
                  <Tr>
                    <Th>CVE ID</Th>
                    <Th>Title</Th>
                    <Th>Severity</Th>
                    <Th>CVSS Score</Th>
                    <Th>Affected Assets</Th>
                  </Tr>
                </Thead>
                <tbody>
                  {mockVulnerabilities.map((vuln) => (
                    <Tr key={vuln.id}>
                      <Td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{vuln.id}</Td>
                      <Td style={{ maxWidth: '300px' }}>{vuln.title}</Td>
                      <Td>
                        <VulnBadge severity={vuln.severity}>{vuln.severity.toUpperCase()}</VulnBadge>
                      </Td>
                      <Td style={{ fontWeight: '700' }}>{vuln.cvss}</Td>
                      <Td>{vuln.affectedHosts}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          </>
        )}

        {activeTab === 'compliance' && (
          <PlaceholderContent
            icon={CheckCircle}
            title="Compliance"
            description="Compliance reporting and audit trails"
          />
        )}
      </MainContent>

      {showNewScanModal && (
        <NewScanModal
          onClose={() => setShowNewScanModal(false)}
          onSubmit={handleNewScan}
        />
      )}

      {showReportModal && (
        <ScanReportModal
          onClose={() => setShowReportModal(false)}
          scan={selectedScan}
        />
      )}
    </Container>
  );
};

export default InternalScansPage;